import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "65";
const height = "42";
const viewBox = "0 0 65 42";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.secondary.orca}
            fillRule="evenodd"
            d="M19.677 1.408C21.961.99 24.22.89 26.485 1.352c2.105.44 3.913 1.439 5.498 2.924 1.728 1.614 3.025 3.562 4.092 5.705 1.222 2.475 2.079 5.065 2.473 7.807.06.442.107.883.14 1.322a3.032 3.032 0 00-.405 1.537c0 .51.123 1.057.392 1.518a19.758 19.758 0 01-.654 3.656 56.225 56.225 0 01-.524 1.635l-.083.249a39.84 39.84 0 00-.312.955c-1.415 3.227-2.935 5.653-5.034 7.641l-.002.001c-2.326 2.22-5.087 3.4-8.27 3.674-2.643.225-5.222-.085-7.774-.849h-.003c-2.6-.763-4.946-1.954-6.982-3.758-1.677-1.51-2.97-3.317-3.979-5.36-1.57-3.163-2.62-6.478-3.102-9.984-.309-2.264-.322-4.48.251-6.646v-.002c.535-2.063 1.62-3.793 3.138-5.28 1.51-1.479 3.265-2.62 5.158-3.58 2.921-1.452 5.972-2.534 9.173-3.109h.001zm19.915 21.616a20.858 20.858 0 01-.61 3.069l-.002.01c-.178.592-.368 1.17-.534 1.67l-.086.255-.003.01-.098.292h.079l-.307.7c-1.45 3.316-3.04 5.88-5.275 7.996-2.5 2.387-5.479 3.654-8.874 3.947h-.001c-2.78.236-5.484-.092-8.145-.888-2.718-.797-5.2-2.052-7.363-3.969l-.003-.002c-1.786-1.608-3.152-3.523-4.208-5.662C2.547 27.198 1.463 23.78.966 20.161c-.32-2.344-.343-4.701.274-7.036.586-2.261 1.775-4.146 3.406-5.743 1.603-1.57 3.451-2.767 5.407-3.757l.003-.002c2.993-1.489 6.135-2.605 9.442-3.199 2.37-.433 4.764-.546 7.188-.052h.002c2.303.483 4.273 1.576 5.978 3.174 1.84 1.719 3.203 3.775 4.305 5.991v.002c1.264 2.558 2.156 5.25 2.567 8.108v.002c.028.204.054.408.076.612.278-.128.603-.202.98-.202 1.591 0 2.29 1.312 2.29 2.428v.443h.018-3.165c-.006.326-.02.651-.04.976.21.255.52.408.914.408.56 0 1.05-.32 1.294-.851l.875.354c-.508.992-1.085 1.418-2.187 1.418-.386 0-.719-.077-1-.21zm.142-2.962h2.083c-.087-.62-.507-1.099-1.207-1.099-.38 0-.69.154-.903.414.013.229.022.457.027.685zm-19.139-3.899v1.046h2.187v5.955h1.085V17.21h2.17v-1.046h-5.442zm13.244 6.434c.245.354.753.638 1.33.638 1.453 0 2.24-.992 2.24-2.606 0-1.63-.77-2.587-2.223-2.587-.594 0-1.154.32-1.347.656v-.585h-1.032v7.072h1.032v-2.587zm2.135-3.19c.227.301.35.709.35 1.223 0 .514-.123.921-.35 1.223a1.09 1.09 0 01-.91.443c-.367 0-.665-.142-.91-.443-.227-.302-.35-.71-.35-1.223 0-.514.105-.922.35-1.223.228-.32.543-.461.91-.461s.665.16.91.46zm7.768-1.294V19.052h.648v4.112h1.032v-4.112h1.102v-.922H45.42v-.336c0-.532.228-.798.666-.798.21 0 .402.053.577.142l.21-.886c-.227-.125-.49-.178-.823-.178-1.032 0-1.661.585-1.661 1.773v.266h-.648zm4.094.673c-.454.497-.664 1.117-.664 1.844 0 .744.227 1.364.664 1.842.438.497 1.05.745 1.785.745.734 0 1.33-.248 1.767-.745.437-.496.665-1.116.665-1.843 0-.726-.228-1.347-.665-1.843-.437-.496-1.032-.744-1.767-.744-.735 0-1.347.248-1.785.744zm.77 3.03a2.061 2.061 0 01-.35-1.186c0-.444.123-.833.35-1.17.245-.32.578-.497.997-.497.42 0 .752.16.997.497.245.319.368.709.368 1.17 0 .46-.123.85-.368 1.187-.245.337-.577.497-.997.497-.42 0-.752-.16-.997-.497zm4.427 1.33h1.032v-1.86c0-.745.14-1.312.402-1.72.263-.407.578-.602.945-.602.227 0 .42.035.578.141l.174-.957a1.484 1.484 0 00-.63-.124c-.56 0-1.137.337-1.47.922v-.833h-1.03v5.033zm5.214-5.033H57.197v5.05h1.031V21.25c0-1.436.49-2.287 1.155-2.287.455 0 .735.284.735.816v3.367h1.032v-2.02c0-1.33.508-2.18 1.155-2.18.455 0 .735.283.735.815v3.367h1.032V19.53c0-.904-.525-1.506-1.417-1.506-.63 0-1.19.373-1.54 1.046-.14-.638-.7-1.046-1.364-1.046-.612 0-1.102.32-1.504.94v-.851zm-27.574 0h1.19l-3.238 7.072h-1.189l.945-2.056-2.17-5.016h1.172l1.593 3.686 1.697-3.686z"
            clipRule="evenodd"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Typeform"
});
